import { useState, type RefObject } from "react";
import { AlertTriangle, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { BuildingDataHistoryModal } from "@/components/building-data/BuildingDataHistoryModal";
import { BuildingDataInspector } from "@/components/building-data/BuildingDataInspector";
import {
  BuildingDataReviewQueue,
  type ReviewQueueItem,
} from "@/components/building-data/BuildingDataReviewQueue";
import { Group } from "@/components/building-data/Group";
import { ChatLauncherSpacer } from "@/components/layout/ChatLauncherSpacer";
import type { useDocumentPreview } from "@/components/chat/SidebarChat/DocLink/useDocumentPreview";
import { Button } from "@/components/ui/button";
import { DocumentViewerDialog } from "@/domains/documents/components/DocumentViewerDialog";
import type { WidgetField, WidgetGroup } from "@/domains/buildings/types/buildingWidget";
import type { useBuildingWidgetData } from "@/hooks/buildings/useBuildingWidgetData";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import { cn } from "@/lib/utils";
import { BuildingDataPageHeader, type StateFilter } from "./BuildingDataPageHeader";

function BuildingDataLoadError({
  tw,
  onRetry,
}: {
  tw: BuildingDataWidgetSections;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="mx-auto mt-12 flex max-w-md flex-col items-center rounded-lg border border-destructive/30 bg-destructive/10 px-6 py-8 text-center"
    >
      <AlertTriangle className="h-7 w-7 text-destructive" />
      <p className="mt-3 text-sm font-semibold text-destructive">{tw.dataLoadError}</p>
      <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
        {tw.retryData}
      </Button>
    </div>
  );
}

const SECTION_NAV_COLLAPSED_KEY = "building-data-section-nav-collapsed:v1";

function initialSectionNavCollapsed(): boolean {
  try {
    return localStorage.getItem(SECTION_NAV_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function persistSectionNavCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(SECTION_NAV_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    // Private mode / storage quota — the toggle still works for this session.
  }
}

function SectionNavigation({
  groups,
  activeSection,
  onSelect,
}: {
  groups: WidgetGroup[];
  activeSection: string | null;
  onSelect: (group: WidgetGroup) => void;
}) {
  const { t } = useLanguage();
  const tw = t.buildings.buildingDataWidget;
  const [collapsed, setCollapsed] = useState(initialSectionNavCollapsed);
  // Persist outside the updater — React may run an updater more than once for
  // one update (it does exactly that under StrictMode), and a double write is
  // the mild version: a discarded replay would desync storage from the UI.
  const toggle = () => {
    const next = !collapsed;
    persistSectionNavCollapsed(next);
    setCollapsed(next);
  };
  if (collapsed) {
    return (
      <nav className="hidden h-full w-10 shrink-0 py-4 min-[1280px]:block">
        <button
          type="button"
          onClick={toggle}
          aria-label={tw.expandSections}
          title={tw.expandSections}
          className="mx-auto flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          <PanelLeftOpen size={15} />
        </button>
      </nav>
    );
  }
  return (
    <nav className="hidden h-full w-[212px] shrink-0 overflow-y-auto border-r border-border bg-muted/15 px-3 py-4 min-[1280px]:block">
      <div className="mb-2 flex items-center justify-between gap-2 pl-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {tw.sections}
        </span>
        <button
          type="button"
          onClick={toggle}
          aria-label={tw.collapseSections}
          title={tw.collapseSections}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          <PanelLeftClose size={14} />
        </button>
      </div>
      <div className="space-y-1">
        {groups.map((group) => {
          const confirmed = group.fields.filter((field) => field.state === "confirmed").length;
          const pending = group.fields.filter((field) => field.state === "pending").length;
          const total = group.fields.length || 1;
          return (
            <button
              key={group.id}
              type="button"
              onClick={() => onSelect(group)}
              className={cn(
                "w-full rounded-md px-2 py-2 text-left transition-colors hover:bg-background",
                activeSection === group.id && "bg-background shadow-sm ring-1 ring-border"
              )}
            >
              <div className="flex items-start gap-1.5">
                <span className="min-w-0 flex-1 text-xs font-medium leading-4 text-foreground">
                  {group.title}
                </span>
                {pending > 0 ? (
                  <span className="rounded-full bg-warning/20 px-1.5 py-0.5 text-[10px] font-semibold text-warning-900">
                    {pending}
                  </span>
                ) : null}
              </div>
              <div className="mt-2 flex h-1 overflow-hidden rounded-full bg-muted/50">
                <span className="bg-success" style={{ width: `${(confirmed / total) * 100}%` }} />
                <span className="bg-warning" style={{ width: `${(pending / total) * 100}%` }} />
              </div>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function emptyFieldsMessage(
  query: string,
  activeFilter: StateFilter,
  tw: BuildingDataWidgetSections
): string {
  if (query.trim()) return tw.noFieldsMatch.replace("{query}", query.trim());
  if (activeFilter === "all") return tw.noFieldsYet;
  return tw.noFieldsForFilter;
}

interface BuildingDataFieldListProps {
  isLoading: boolean;
  isError: boolean;
  groups: WidgetGroup[];
  query: string;
  activeFilter: StateFilter;
  tw: BuildingDataWidgetSections;
  registryCode: string | null;
  orgId: string | undefined;
  buildingId: string;
  canManage: boolean;
  selectedFieldId: string | null;
  onRetry: () => void;
  onDocPreview: (filename: string) => void;
  onFieldSelect: (field: WidgetField) => void;
  onConfirmAll: (fields: WidgetField[]) => Promise<void>;
  openGroupIds: readonly string[];
  onGroupToggle: (groupId: string) => void;
}

function BuildingDataFieldList(props: BuildingDataFieldListProps) {
  if (props.isLoading) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">{props.tw.loading}</div>
    );
  }
  if (props.isError) return <BuildingDataLoadError tw={props.tw} onRetry={props.onRetry} />;
  if (props.groups.length === 0) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">
        {emptyFieldsMessage(props.query, props.activeFilter, props.tw)}
      </div>
    );
  }
  return (
    <div>
      {props.groups.map((group) => (
        <Group
          key={group.id}
          group={group}
          open={props.openGroupIds.includes(group.id)}
          onToggle={() => props.onGroupToggle(group.id)}
          registryCode={props.registryCode}
          onDocPreview={props.onDocPreview}
          orgId={props.orgId}
          buildingId={props.buildingId}
          canManage={props.canManage}
          onFieldSelect={props.onFieldSelect}
          selectedFieldId={props.selectedFieldId}
          onConfirmAll={props.onConfirmAll}
        />
      ))}
    </div>
  );
}

function BuildingDataInspectorPane(props: {
  open: boolean;
  field: WidgetField | null;
  group: WidgetGroup | null;
  fallbackGroupTitle: string;
  buildingId: string;
  orgId: string | undefined;
  registryCode: string | null;
  canManage: boolean;
  onClose: () => void;
  onDocPreview: (filename: string) => void;
}) {
  if (!props.open || !props.field) return null;
  return (
    <BuildingDataInspector
      field={props.field}
      groupTitle={props.group?.title ?? props.fallbackGroupTitle}
      buildingId={props.buildingId}
      orgId={props.orgId}
      registryCode={props.registryCode}
      canManage={props.canManage}
      onClose={props.onClose}
      onDocPreview={props.onDocPreview}
    />
  );
}

function BuildingDataReviewOverlay(props: {
  open: boolean;
  canManage: boolean;
  items: ReviewQueueItem[];
  buildingId: string;
  orgId: string | undefined;
  registryCode: string | null;
  documentPreviewOpen: boolean;
  onClose: () => void;
  onDocPreview: (filename: string) => void;
  onSelectField: (field: WidgetField) => void;
}) {
  if (!props.open || !props.canManage) return null;
  return (
    <BuildingDataReviewQueue
      items={props.items}
      buildingId={props.buildingId}
      orgId={props.orgId}
      registryCode={props.registryCode}
      canManage={props.canManage}
      documentPreviewOpen={props.documentPreviewOpen}
      onClose={props.onClose}
      onDocPreview={props.onDocPreview}
      onSelectField={props.onSelectField}
    />
  );
}

type BuildingWidgetData = ReturnType<typeof useBuildingWidgetData>["data"];
type DocumentPreviewState = ReturnType<typeof useDocumentPreview>;

export interface BuildingDataPageLayoutProps {
  title: string;
  tw: BuildingDataWidgetSections;
  data: BuildingWidgetData;
  isLoading: boolean;
  isError: boolean;
  groups: WidgetGroup[];
  filteredGroups: WidgetGroup[];
  reviewItems: ReviewQueueItem[];
  selectedField: WidgetField | null;
  selectedGroup: WidgetGroup | null;
  selectedFieldId: string | null;
  inspectorOpen: boolean;
  reviewOpen: boolean;
  historyOpen: boolean;
  activeSection: string | null;
  activeFilter: StateFilter;
  query: string;
  buildingId: string;
  orgId: string | undefined;
  registryCode: string | null;
  canManage: boolean;
  documentPreview: DocumentPreviewState;
  historyTriggerRef: RefObject<HTMLButtonElement>;
  onSearchInputMount: (element: HTMLInputElement | null) => void;
  onContentScrollMount: (element: HTMLDivElement | null) => void;
  onRetry: () => void;
  onFilterChange: (filter: StateFilter) => void;
  onQueryChange: (query: string) => void;
  onOpenReview: () => void;
  onCloseReview: () => void;
  onOpenHistory: () => void;
  onHistoryOpenChange: (open: boolean) => void;
  onCloseInspector: () => void;
  onSelectGroup: (group: WidgetGroup) => void;
  onDocPreview: (filename: string) => void;
  onFieldSelect: (field: WidgetField) => void;
  onConfirmAll: (fields: WidgetField[]) => Promise<void>;
  openGroupIds: readonly string[];
  onGroupToggle: (groupId: string) => void;
}

function PageHeader(props: BuildingDataPageLayoutProps) {
  return (
    <BuildingDataPageHeader
      title={props.title}
      tw={props.tw}
      isError={props.isError}
      totalCount={props.data?.totalCount}
      confirmedCount={props.data?.confirmedCount}
      pendingCount={props.data?.pendingCount}
      emptyCount={props.data?.emptyCount}
      lastUpdated={props.data?.lastUpdated ?? null}
      activeFilter={props.activeFilter}
      query={props.query}
      reviewCount={props.reviewItems.length}
      canManage={props.canManage}
      historyTriggerRef={props.historyTriggerRef}
      onSearchInputMount={props.onSearchInputMount}
      onRetry={props.onRetry}
      onFilterChange={props.onFilterChange}
      onQueryChange={props.onQueryChange}
      onOpenReview={props.onOpenReview}
      onOpenHistory={props.onOpenHistory}
    />
  );
}

function PageFieldPane(props: BuildingDataPageLayoutProps) {
  const { onContentScrollMount, ...contentProps } = props;
  return (
    <main ref={onContentScrollMount} className="min-w-0 flex-1 overflow-y-auto">
      <PageFieldPaneContent {...contentProps} />
    </main>
  );
}

type PageFieldPaneContentProps = Omit<BuildingDataPageLayoutProps, "onContentScrollMount">;

function PageFieldPaneContent(props: PageFieldPaneContentProps) {
  return (
    <div className="w-full max-w-[980px] px-6 pt-4">
      <BuildingDataFieldList
        isLoading={props.isLoading}
        isError={props.isError}
        groups={props.filteredGroups}
        query={props.query}
        activeFilter={props.activeFilter}
        tw={props.tw}
        registryCode={props.registryCode}
        orgId={props.orgId}
        buildingId={props.buildingId}
        canManage={props.canManage}
        selectedFieldId={props.selectedFieldId}
        onRetry={props.onRetry}
        onDocPreview={props.onDocPreview}
        onFieldSelect={props.onFieldSelect}
        onConfirmAll={props.onConfirmAll}
        openGroupIds={props.openGroupIds}
        onGroupToggle={props.onGroupToggle}
      />
      <ChatLauncherSpacer />
    </div>
  );
}

function PageWorkspace(props: BuildingDataPageLayoutProps) {
  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <SectionNavigation
        groups={props.groups}
        activeSection={props.activeSection}
        onSelect={props.onSelectGroup}
      />
      <PageFieldPane {...props} />
      <BuildingDataInspectorPane
        open={props.inspectorOpen}
        field={props.selectedField}
        group={props.selectedGroup}
        fallbackGroupTitle={props.tw.allFields}
        buildingId={props.buildingId}
        orgId={props.orgId}
        registryCode={props.registryCode}
        canManage={props.canManage}
        onClose={props.onCloseInspector}
        onDocPreview={props.onDocPreview}
      />
    </div>
  );
}

function PageOverlays(props: BuildingDataPageLayoutProps) {
  const preview = props.documentPreview;
  return (
    <>
      <BuildingDataReviewOverlay
        open={props.reviewOpen}
        canManage={props.canManage}
        items={props.reviewItems}
        buildingId={props.buildingId}
        orgId={props.orgId}
        registryCode={props.registryCode}
        documentPreviewOpen={preview.isOpen}
        onClose={props.onCloseReview}
        onDocPreview={props.onDocPreview}
        onSelectField={props.onFieldSelect}
      />
      <BuildingDataHistoryModal
        open={props.historyOpen}
        onOpenChange={props.onHistoryOpenChange}
        buildingId={props.buildingId}
        orgId={props.orgId}
        groups={props.groups}
        registryCode={props.registryCode}
        onDocPreview={props.onDocPreview}
        returnFocusRef={props.historyTriggerRef}
      />
      <DocumentViewerDialog
        document={preview.document}
        blobUrl={preview.blobUrl}
        blobData={preview.blobData}
        open={preview.isOpen}
        onOpenChange={(open) => {
          if (!open) preview.closePreview();
        }}
        originalDwgFilePath={preview.originalDwgFilePath ?? undefined}
      />
    </>
  );
}

export function BuildingDataPageLayout(props: BuildingDataPageLayoutProps) {
  return (
    <div className="building-data-widget flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader {...props} />
      <PageWorkspace {...props} />
      <PageOverlays {...props} />
    </div>
  );
}
